import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'

import { Loader } from '@buffer-mono/popcorn'

import { buildAnalyzeHomeUrl } from '~publish/helpers/externalUrls'
import { navigateTo } from '~publish/helpers/navigation'
import { useAnalyzeSunsetReady } from '~publish/hooks/useAnalyzeSunsetEnabled'

import type { JSX } from 'react'

/**
 * Resolves the legacy `/analyze` route.
 *
 * Once Analyze is retired, a bookmark or a link in an already-sent email should
 * land on the replacement surface rather than a dead end, so the route
 * redirects in-app to Insights. While the flag is off it keeps sending the
 * customer out to analyze.buffer.com, as it always has.
 *
 * This route acts on its first render, so it waits for the Split SDK rather
 * than reading the pre-ready `false`: acting early would fire an external
 * navigation to analyze.buffer.com that cannot be undone once the real value
 * lands.
 *
 * @see EPD-1939
 */
export function AnalyzeRedirect(): JSX.Element {
  const { isEnabled: isAnalyzeSunset, isReady } = useAnalyzeSunsetReady()

  useEffect(() => {
    if (!isReady || isAnalyzeSunset) return
    navigateTo(buildAnalyzeHomeUrl())
  }, [isReady, isAnalyzeSunset])

  if (isReady && isAnalyzeSunset) return <Navigate to="/insights" replace />

  // Every other path is either waiting on the SDK or has just handed off to a
  // full-page navigation. Render the loader rather than an empty fragment, so
  // `/analyze` never flashes a blank page.
  return <Loader />
}
